import React, { useState } from 'react'
import { Checkbox } from "@/components/ui/checkbox"
import { Edit2, Save, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useStore } from '@/store'
import { updateQuestions } from '@/api/api'
import { useToast } from "@/hooks/use-toast"

interface QuestionProps {
  id: string
  question: string
  insightId: number
  allQuestions: string[]
  questionIndex: number
  onQuestionsUpdate: (questions: string[]) => void
}

export function Question({ id, question: initialQuestion, insightId, allQuestions, questionIndex, onQuestionsUpdate }: QuestionProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedQuestion, setEditedQuestion] = useState(initialQuestion)
  const { selectedDots, toggleDotSelection } = useStore()
  const { toast } = useToast()

  const isSelected = selectedDots.some(
    dot => dot.id === id && dot.type === 'question'
  )

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation()
    
    const updatedQuestions = [...allQuestions]
    updatedQuestions[questionIndex] = editedQuestion

    try {
      const result = await updateQuestions(insightId, updatedQuestions)
      
      if (result.success) {
        setIsEditing(false)
        onQuestionsUpdate(updatedQuestions)
        
        toast({
          title: "Success",
          description: "Question updated successfully",
        })
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to update question",
          variant: "destructive",
        })
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update question",
        variant: "destructive",
      })
    }
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    
    const updatedQuestions = allQuestions.filter((_, index) => index !== questionIndex)

    try {
      const result = await updateQuestions(insightId, updatedQuestions)
      
      if (result.success) {
        onQuestionsUpdate(updatedQuestions)
        toast({
          title: "Success",
          description: "Question deleted successfully",
        })
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to delete question",
          variant: "destructive",
        })
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete question",
        variant: "destructive",
      })
    }
  }

  return (
    <div className="flex items-center gap-2 w-full py-0.5">
      <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
        <Checkbox 
          checked={isSelected}
          onCheckedChange={() => {
            toggleDotSelection({
              id: id,
              type: 'question',
              name: initialQuestion
            })
          }}
        />
      </div>
      
      {isEditing ? (
        <div className="flex-1 flex items-center gap-2">
          <input
            type="text"
            value={editedQuestion}
            onChange={(e) => setEditedQuestion(e.target.value)}
            className="flex-1 text-sm text-muted-foreground bg-background border rounded px-2 py-0.5"
            autoFocus
          />
          <Button
            variant="ghost"
            className="h-6 px-2 opacity-100"
            onClick={handleSave}
          >
            <Save className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-between group">
          <span className="text-sm text-muted-foreground">{initialQuestion}</span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              className="h-6 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={handleEditClick}
            >
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              className="h-6 px-2 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
              onClick={handleDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
} 